import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import { createTheme, styled } from "@mui/material/styles";
import { DataGrid, faIR } from "@mui/x-data-grid";
import { deepOrange } from "@mui/material/colors";
import { Box } from "@mui/material";

export const COLORS = {
  RED: "#F44336",
  WHITE: "#FFFF",
  SOFTRED: "#FFEBEE",
  BLACK: "#212121",
  SOFTBLACK: "#616161",
  BLUE: "#2196F3",
  SOFTBLUE: "#E3F2FD",
  GRAY: "#9E9E9E",
  SOFTGRAY: "#f5f5f5",
  DARKGRAY: "#757575",
  BG: "#F2F2F7",
  GREEN: "#4CAF50",
  SOFTGREEN: "#E8F5E9",
  YELLOW: "#FFEB3B",
  SOFTYELLOW: "#FFFDE7",
  ORANGE: "#FF9800",
  SOFTORANGE: "#FFF3E0",
  INPUTBG: "#00000008",
};

//RTL
export const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

export const lightTheme = createTheme({
  direction: "rtl",
  palette: {
    primary: {
      main: COLORS.BLUE,
    },
    background: {
      default: COLORS.BG,
    },
  },
  typography: {
    fontSize: 13,
    fontFamily: ["Vazir FD-WOL", "Poppins"].join(","),
  },
  components: {
    MuiButton: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiDataGrid: {
      defaultProps: {
        initialState: {
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        },
        localeText: {
          ...faIR.components.MuiDataGrid.defaultProps.localeText,
        },
        density: "compact",
        getRowClassName: (params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "odd-row" : "even-row",
      },
      styleOverrides: {
        menu: {
          "& .MuiPaper-root": {
            boxShadow: "0 16px 32px -4px #0C0C0D1A",
            zIndex: 2,
            minWidth: "187px !important",
            borderRadius: "10px",
            backgroundColor: COLORS.WHITE,
            padding: "0 12px",
          },
        },
        columnHeaders: {
          fontWeight: "bold",
        },
        root: {
          backgroundColor: COLORS.WHITE,
          "& .MuiButton-root": {
            fontWeight: 400,
            boxShadow: "none",
            backgroundColor: "transparent",
            color: "rgba(0, 0, 0, 0.87)",
            borderRadius: "4px",
            fontSize: "0.75rem",
            padding: "5px 10px",
            "&:hover": {
              boxShadow: "none",
              backgroundColor: "rgba(15, 53, 66, 0.04)",
            },
            "&:active": {
              backgroundColor: "rgba(15, 53, 66, 0.04)",
            },
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            border: "none",
            height: "46px",
            borderRadius: "12px",
            fontSize: "14px",
            padding: "0 12px",
            boxShadow: "none",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            display: "none",
          },
          "& .MuiInputBase-input": {
            padding: "0",
          },
          "& .MuiFormLabel-root": {
            fontSize: "14px",
          },
          "& .MuiInputAdornment-root": {
            fontSize: "14px",
          },
        },
        listbox: {
          paddingTop: "0px",
          paddingBottom: "0px",
          padding: "0px",
        },
        popper: {
          backgroundColor: COLORS.WHITE,
          boxShadow: "0 16px 32px -4px #0C0C0D1A",
          borderRadius: "12px",
        },
        option: {
          fontSize: "14px",
          backgroundColor: COLORS.SOFTGRAY,
          borderRadius: "8px",
          marginBottom: "5px",

          "&:hover": {
            backgroundColor: COLORS.SOFTBLUE,
            color: COLORS.BLUE,
          },
          "&.Mui-selected": {
            backgroundColor: `${COLORS.SOFTBLUE} !important`,
            color: `${COLORS.BLUE} !important`,
          },
          "&.Mui-selected:hover": {
            backgroundColor: `${COLORS.SOFTBLUE} !important`,
            color: `${COLORS.BLUE} !important`,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "14px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            border: "none",
            height: "46px",
            borderRadius: "12px",
            backgroundColor: COLORS.INPUTBG,
            fontSize: "14px",
            padding: "0 12px",
            boxShadow: "none",
          },

          "& .MuiOutlinedInput-notchedOutline": {
            display: "none",
          },
          "& .MuiInputBase-input": {
            padding: "0",
          },
          "& .MuiFormLabel-root": {
            fontSize: "14px",
          },
          "& .MuiInputAdornment-root": {
            fontSize: "14px",
          },
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0px 1000px #f5f5f5 inset",
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          backgroundColor: `#00000008 !important`,
          border: "none",
          height: "46px",
          fontSize: "14px",
          padding: "0 12px",
          boxShadow: "none",
          borderRadius: "12px",

          "& .MuiOutlinedInput-notchedOutline": {
            display: "none",
          },
        },
        icon: {
          fontSize: "14px",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          padding: "24px 14px",
          borderRadius: "12px",
          backgroundColor: "#FAFAFA",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          backgroundColor: "#FFFFFF",
          padding: "10px 12px",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: "0 16px 32px -4px #0C0C0D1A",
          zIndex: 2,
          borderRadius: "10px",
          backgroundColor: COLORS.WHITE,
          padding: "0 12px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          margin: "5px 0",
          padding: "6px",
          borderRadius: "7px",
          fontSize: "12px",
          minHeight: "unset",
          backgroundColor: COLORS.SOFTGRAY,
          "&.Mui-selected": {
            backgroundColor: `${COLORS.SOFTBLUE} !important`,
            color: `${COLORS.BLUE} !important`,
          },
          "&.Mui-selected:hover": {
            backgroundColor: `${COLORS.SOFTBLUE} !important`,
            color: `${COLORS.BLUE} !important`,
          },
          "&:hover": {
            backgroundColor: COLORS.SOFTBLUE,
            color: COLORS.BLUE,
          },
        },
      },
    },
  },
});

export const DataGridBox = styled(DataGrid)(({ theme }) => ({
  borderRadius: "12px",
  border: 0,

  "& .MuiDataGrid-toolbarContainer": {
    borderRadius: "12px 12px 0 0",
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#18202d",
    padding: "16px",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#18202d",
    borderRadius: 0,
  },
  "& .odd-row": {
    minHeight: "30px !important",
    backgroundColor: theme.palette.mode === "light" ? COLORS.WHITE : "#18202d",
  },
  "& .even-row": {
    minHeight: "30px !important",
    backgroundColor:
      theme.palette.mode === "light" ? COLORS.INPUTBG : "#18202d",
  },

  "& .MuiDataGrid-row:hover": {
    background: COLORS.SOFTBLUE,
  },
  "& .selected-row": {
    backgroundColor: deepOrange[100],
  },
  "& .MuiDataGrid-footerContainer": {
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#18202d",
    padding: "8px 12px",
    borderBottomLeftRadius: "12px",
    borderBottomRightRadius: "12px",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bold !important",
  },
}));

export const ChartTooltip = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  gap: "5px",
  textAlign: "center",
  fontWeight: 900,
  borderRadius: "4px",
  padding: "4px 8px",
  boxShadow: "0 16px 32px -4px #0C0C0D1A",
  background: COLORS.SOFTBLUE,
}));
